/* eslint-disable @typescript-eslint/no-use-before-define */

import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ttcThisWeekNextWeekSvg from 'src/public/img/time-to-care-this-week-next-week.svg';
import ttcTodayTomorrowSvg from 'src/public/img/time-to-care-today-tomorrow.svg';
import { color, font } from 'src/shared/styles/variables';

const isProviderAvailableTodayTmrw = (type): boolean => {
    switch (type) {
        case ProviderAvailableTodayTmrw.AVAILABLE_TODAY:
        case ProviderAvailableTodayTmrw.AVAILABLE_TOMORROW:
            return true;
        case ProviderAvailableThisWeekNextWeek.AVAILABLE_THIS_WEEK:
        case ProviderAvailableThisWeekNextWeek.AVAILABLE_NEXT_WEEK:
        case ProviderAvailableWithinAMonth.AVAILABLE_WITHIN_A_MONTH:
        case ProviderWithoutCalendar.CONTACT_FOR_AVAILABILITY:
            return false;
        default:
            return true;
    }
};

const getAvailabilityIcon = type => {
    switch (type) {
        case ProviderAvailableTodayTmrw.AVAILABLE_TODAY:
        case ProviderAvailableTodayTmrw.AVAILABLE_TOMORROW:
            return ttcTodayTomorrowSvg;
        case ProviderAvailableThisWeekNextWeek.AVAILABLE_THIS_WEEK:
        case ProviderAvailableThisWeekNextWeek.AVAILABLE_NEXT_WEEK:
        case ProviderAvailableWithinAMonth.AVAILABLE_WITHIN_A_MONTH:
        case ProviderWithoutCalendar.CONTACT_FOR_AVAILABILITY:
            return ttcThisWeekNextWeekSvg;
        default:
            return undefined;
    }
};

interface Props {
    providerAvailability?: string | null;
    isLoading?: boolean;
}

const AavailabilityIconWrapper = styled.div({
    height: 24,
    width: 12,
    borderRadius: 12,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
});

const AvailabilityText = styled.p<Props>(props => ({
    color: isProviderAvailableTodayTmrw(props.providerAvailability)
        ? color.cornflowerBlueDark
        : color.blueBayoux,
    flex: 1,
    textAlign: 'left',
    lineHeight: '20px',
    marginTop: 2,
    ...font.heavy(font.size.base),
}));

const IconImage = styled('img')({
    height: 12,
    width: 12,
    objectFit: 'contain',
    color: color.cornflowerBlueDark,
});

const ProviderInfoRow = styled.div({
    display: 'flex',
    alignItems: 'center',
});

export enum ProviderAvailabilityTypes {
    AVAILABLE_TODAY = 'Available Today',
    AVAILABLE_TOMORROW = 'Available Tomorrow',
    AVAILABLE_THIS_WEEK = 'Available This Week',
    AVAILABLE_NEXT_WEEK = 'Available Next Week',
}

export enum ProviderAvailableTodayTmrw {
    AVAILABLE_TODAY = 'AVAILABLE_TODAY',
    AVAILABLE_TOMORROW = 'AVAILABLE_TOMORROW',
}

export enum ProviderAvailableThisWeekNextWeek {
    AVAILABLE_THIS_WEEK = 'AVAILABLE_THIS_WEEK',
    AVAILABLE_NEXT_WEEK = 'AVAILABLE_NEXT_WEEK',
}

export enum ProviderAvailableWithinAMonth {
    AVAILABLE_WITHIN_A_MONTH = 'AVAILABLE_WITHIN_A_MONTH',
}

export enum ProviderWithoutCalendar {
    CONTACT_FOR_AVAILABILITY = 'CONTACT_FOR_AVAILABILITY',
}

export enum ProviderAvailabilityTestID {
    Skeleton = 'ProviderAvailability.Skeleton',
    AvailabilityIcon = 'ProviderAvailability.AvailabilityIcon',
    AvailabilityText = 'ProviderAvailability.AvailabilityText',
}

export const ProviderAvailability: React.FC<Props> = ({ providerAvailability = '' }) => {
    const { t } = useTranslation();

    return (
        <ProviderInfoRow>
            <AavailabilityIconWrapper data-testid={ProviderAvailabilityTestID.AvailabilityIcon}>
                <IconImage src={getAvailabilityIcon(providerAvailability)} />
            </AavailabilityIconWrapper>
            <AvailabilityText
                data-testid={ProviderAvailabilityTestID.AvailabilityText}
                providerAvailability={providerAvailability}
            >
                {providerAvailability &&
                    t(`providers:provider_availability_type.${providerAvailability.toLowerCase()}`)}
            </AvailabilityText>
        </ProviderInfoRow>
    );
};
